import React, { useState, useRef } from 'react';
import { extractLaptopSpecs, ExtractedLaptopSpecs, normalizeSpecValue } from '@/services/aiExtraction';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import {
  Sparkles,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
  Info,
  Camera,
} from 'lucide-react';

interface LaptopSpecExtractorModalProps {
  onConfirm: (specs: ExtractedLaptopSpecs) => void;
  onCancel: () => void;
}

export const LaptopSpecExtractorModal: React.FC<LaptopSpecExtractorModalProps> = ({
  onConfirm,
  onCancel,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<'upload' | 'extracting' | 'review' | 'not_configured'>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [extractError, setExtractError] = useState<string | null>(null);

  // Editable spec fields
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [serviceTag, setServiceTag] = useState('');
  const [cpu, setCpu] = useState('');
  const [ram, setRam] = useState('');
  const [storage, setStorage] = useState('');
  const [gpu, setGpu] = useState('');
  const [screenSize, setScreenSize] = useState('');
  const [colour, setColour] = useState('');
  const [operatingSystem, setOperatingSystem] = useState('');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setExtractError(null);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const handleExtract = async () => {
    if (!selectedFile) return;
    setStep('extracting');
    setExtractError(null);

    const result = await extractLaptopSpecs(selectedFile);

    if (result.notConfigured) {
      setStep('not_configured');
      return;
    }

    if (result.error || !result.data) {
      setStep('upload');
      setExtractError(result.error ?? 'Extraction failed');
      return;
    }

    const d = result.data;
    setBrand(d.brand ?? '');
    setModel(d.model ?? '');
    setSerialNumber(d.serial_number ?? '');
    setServiceTag(d.service_tag ?? '');
    setCpu(d.cpu ?? '');
    setRam(normalizeSpecValue(d.ram) ?? d.ram ?? '');
    setStorage(normalizeSpecValue(d.storage) ?? d.storage ?? '');
    setGpu(d.gpu ?? '');
    setScreenSize(d.screen_size ?? '');
    setColour(d.colour ?? '');
    setOperatingSystem(d.operating_system ?? '');
    setStep('review');
  };

  const handleConfirm = () => {
    onConfirm({
      brand: brand.trim() || null,
      model: model.trim() || null,
      serial_number: serialNumber.trim() || null,
      service_tag: serviceTag.trim() || null,
      cpu: cpu.trim() || null,
      ram: normalizeSpecValue(ram) || ram.trim() || null,
      storage: normalizeSpecValue(storage) || storage.trim() || null,
      gpu: gpu.trim() || null,
      screen_size: screenSize.trim() || null,
      colour: colour.trim() || null,
      operating_system: operatingSystem.trim() || null,
    });
  };

  const inputClass = 'w-full px-2 py-1.5 bg-white border border-brand-border rounded text-xs text-brand-black focus:outline-none focus:ring-1 focus:ring-brand-green';

  // ── Not Configured ──────────────────────────────────────────────────────────
  if (step === 'not_configured') {
    return (
      <div className="p-6 flex flex-col items-center text-center space-y-4">
        <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center">
          <Sparkles className="w-7 h-7 text-amber-500" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-brand-black">AI Extraction Not Configured</h3>
          <p className="text-xs text-brand-muted mt-1 max-w-xs">
            The <strong>GEMINI_API_KEY</strong> secret has not been set on the Supabase Edge Function server.
          </p>
        </div>
        <div className="w-full p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900 text-left space-y-1">
          <p className="font-bold">To enable AI Spec Extraction:</p>
          <p>1. Go to Supabase → Edge Functions → Secrets</p>
          <p>2. Add secret: <code className="font-mono bg-amber-100 px-1 rounded">GEMINI_API_KEY</code> = your Google AI API key</p>
          <p>3. Deploy: <code className="font-mono bg-amber-100 px-1 rounded">supabase functions deploy extract-laptop-specs</code></p>
        </div>
        <button onClick={onCancel} className="px-4 py-2 bg-brand-green text-white font-bold rounded hover:bg-brand-green-hover text-xs">
          Close
        </button>
      </div>
    );
  }

  // ── Upload ──────────────────────────────────────────────────────────────────
  if (step === 'upload' || step === 'extracting') {
    return (
      <div className="space-y-4 text-xs">
        <div className="flex items-start space-x-2 p-2.5 bg-blue-50 border border-blue-200 rounded-lg text-[11px] text-blue-800">
          <Info className="w-3.5 h-3.5 text-blue-600 shrink-0 mt-0.5" />
          <span>
            <strong>AI reads the laptop photo or spec sticker server-side only.</strong> Nothing is saved automatically.
            You review all fields before populating the laptop form.
          </span>
        </div>

        {extractError && <ErrorAlert message={extractError} />}

        <div
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
            selectedFile ? 'border-brand-green bg-green-50/40' : 'border-brand-border hover:border-brand-green hover:bg-green-50/20'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic"
            onChange={handleFileChange}
            className="hidden"
          />

          {previewUrl ? (
            <img src={previewUrl} alt="Laptop preview" className="max-h-48 mx-auto object-contain rounded" />
          ) : (
            <div className="flex flex-col items-center space-y-2 text-brand-muted">
              <Camera className="w-10 h-10 opacity-40" />
              <p className="font-semibold text-brand-black">Click to upload laptop photo or spec label</p>
              <p className="text-[11px]">JPEG, PNG, WEBP, or HEIC — max 10 MB</p>
              <p className="text-[11px] text-brand-muted">
                Best results: bottom sticker, spec sheet, or a clear photo showing model/serial label
              </p>
            </div>
          )}
        </div>

        {selectedFile && (
          <p className="text-[11px] text-brand-muted text-center">
            Selected: <strong className="text-brand-black">{selectedFile.name}</strong>
          </p>
        )}

        <div className="flex items-center justify-end space-x-3 pt-2 border-t border-brand-border">
          <button type="button" onClick={onCancel} disabled={step === 'extracting'} className="px-3 py-2 text-brand-muted hover:text-brand-black font-medium">
            Cancel
          </button>
          <button
            type="button"
            disabled={!selectedFile || step === 'extracting'}
            onClick={handleExtract}
            className="inline-flex items-center space-x-2 px-5 py-2 bg-brand-green text-white font-bold rounded-md hover:bg-brand-green-hover disabled:opacity-50 shadow-sm"
          >
            {step === 'extracting' ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Reading specs with AI...</span>
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                <span>Extract Laptop Specs</span>
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // ── Review ──────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4 text-xs max-h-[78vh] overflow-y-auto pr-1">
      <div className="flex items-center space-x-2 p-2.5 bg-green-50 border border-green-200 rounded-lg text-green-800">
        <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
        <span className="text-[11px] font-semibold">
          AI extracted the fields below from your image. <strong>Review and correct before confirming.</strong>
          Nothing is saved until you confirm and submit the laptop form.
        </span>
      </div>

      {/* Image thumbnail */}
      {previewUrl && (
        <img src={previewUrl} alt="Source" className="h-24 object-contain rounded border border-brand-border" />
      )}

      {/* Editable spec fields */}
      <div className="bg-white border border-brand-border rounded-lg p-3 space-y-3">
        <h4 className="font-bold text-brand-black uppercase tracking-wider">Extracted Laptop Specifications</h4>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            { label: 'Brand', val: brand, setter: setBrand, placeholder: 'e.g. Dell' },
            { label: 'Model', val: model, setter: setModel, placeholder: 'e.g. Inspiron 15 3511' },
            { label: 'Serial Number', val: serialNumber, setter: setSerialNumber, placeholder: 'S/N from sticker' },
            { label: 'Service Tag', val: serviceTag, setter: setServiceTag, placeholder: 'Dell Service Tag' },
            { label: 'CPU / Processor', val: cpu, setter: setCpu, placeholder: 'e.g. Intel Core i5-1135G7' },
            { label: 'RAM', val: ram, setter: setRam, placeholder: 'e.g. 8GB DDR4' },
            { label: 'Storage', val: storage, setter: setStorage, placeholder: 'e.g. 512GB NVMe SSD' },
            { label: 'GPU / Graphics', val: gpu, setter: setGpu, placeholder: 'e.g. NVIDIA GTX 1650' },
            { label: 'Screen Size', val: screenSize, setter: setScreenSize, placeholder: 'e.g. 15.6 inch FHD' },
            { label: 'Colour', val: colour, setter: setColour, placeholder: 'e.g. Black, Silver' },
            { label: 'Operating System', val: operatingSystem, setter: setOperatingSystem, placeholder: 'e.g. Windows 11 Home' },
          ].map(({ label, val, setter, placeholder }) => (
            <div key={label}>
              <label className="block font-bold uppercase text-brand-muted mb-1">{label}</label>
              <input
                value={val}
                onChange={(e) => setter(e.target.value)}
                placeholder={placeholder}
                className={`${inputClass} ${val ? 'border-brand-green bg-green-50/30' : ''}`}
              />
            </div>
          ))}
        </div>

        <p className="text-[11px] text-amber-700 flex items-center space-x-1">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          <span>Always verify extracted specs against the actual hardware before finalising.</span>
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-brand-border">
        <button
          type="button"
          onClick={() => setStep('upload')}
          className="text-brand-muted hover:text-brand-black font-medium text-xs"
        >
          ← Re-upload
        </button>
        <div className="flex items-center space-x-2">
          <button type="button" onClick={onCancel} className="px-3 py-2 text-brand-muted hover:text-brand-black font-medium">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="inline-flex items-center space-x-2 px-5 py-2 bg-brand-green text-white font-bold rounded-md hover:bg-brand-green-hover shadow-sm"
          >
            <ChevronRight className="w-4 h-4" />
            <span>Populate Laptop Form</span>
          </button>
        </div>
      </div>
    </div>
  );
};
